<template>
  <b-container class="mw-100 mt-3 mb-2">
    <b-card>
      <validation-observer ref="mainForm" v-slot="{ invalid }">
        <b-container class="m-0 p-0 mw-100">
          <b-row>
            <b-col cols="12" sm="12" md="6" lg="4" xl="4">
              <b-form-group :label="`${$tc('message.vehicle', 1)} (*)`"
                            label-for="input-vehicle">
                <model-list-select id="input-vehicle"
                                   option-text="text"
                                   option-value="value"
                                   :class="checkData(input.vehicleId).class"
                                   :customText="getVehicleOption"
                                   :placeholder="vehicleOptions && vehicleOptions.length !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                   v-model="input.vehicleId"
                                   :list="vehicleOptions">
                </model-list-select>
                <div v-if="checkData(input.vehicleId).state === false" class="my-invalid-feedback">
                  <h6>{{$tc('message.vehicle', 1)}} {{$tc('message.required', 1).toLowerCase()}}</h6>
                </div>
              </b-form-group>
            </b-col>

            <b-col cols="12" sm="12" md="6" lg="4" xl="4">
              <b-form-group :label="$t('message.driver')"
                            label-for="input-driver">
                <model-list-select id="input-driver"
                                   option-text="text"
                                   option-value="value"
                                   :placeholder="driverOptions && driverOptions.length !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                   v-model="input.driverId"
                                   :list="driverOptions">
                </model-list-select>
              </b-form-group>
            </b-col>

            <b-col cols="12" sm="12" md="6" lg="4" xl="4">
              <b-form-group :label="`${$tc('message.status', 1)} ${$tc('message.accident', 1)}`"
                            label-for="input-status">
                <model-list-select id="input-status"
                                   option-text="text"
                                   option-value="value"
                                   :placeholder="statusOptions && statusOptions.length !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                   v-model="input.state"
                                   :list="statusOptions">
                </model-list-select>
              </b-form-group>
            </b-col>

            <b-col cols="12" sm="12" md="6" lg="3" xl="3">
              <b-form-group>
                <label slot="label" class="m-0 p-0">
                  {{$tc('message.date', 1)}} {{$tc('message.accident', 1)}}(*)
                </label>
                <b-input-group>
                  <flat-pickr id="input-date"
                              ref="date"
                              :placeholder="`${$tc('message.example', 1)}: ${DateTime.local().toFormat($t('message.dateFormat'))}`"
                              :class="changeFlatClass('date', checkData(input.ts).class)"
                              v-model="input.ts"
                              :config="dateOption">
                  </flat-pickr>
                  <div class="input-group-append">
                    <b-button @click="input.ts = null"
                              :disabled="!input.ts">
                      <font-awesome-icon icon="trash" />
                    </b-button>
                  </div>
                  <div v-if="checkData(input.ts).state === false" class="my-invalid-feedback">
                    <h6>{{$tc('message.date', 1)}} {{$tc('message.accident', 1)}} {{$tc('message.required', 2).toLowerCase()}}</h6>
                  </div>
                </b-input-group>
              </b-form-group>
            </b-col>

            <b-col cols="12" sm="12" md="6" lg="3" xl="3">
              <validation-provider :name="$tc('message.place', 1).toLowerCase()"
                                   :rules="{ min: 0, max: 65 }"
                                   v-slot="{ valid, errors }">
                <b-form-group :label="$tc('message.place', 1)" label-for="input-place">
                  <b-input-group>
                    <b-form-input id="input-place"
                                  v-model="input.place"
                                  autocomplete="off"
                                  :placeholder="`${$tc('message.example', 1)}: Roma`"
                                  :state="itemChanged && !valid ? false : null">
                    </b-form-input>
                  </b-input-group>

                  <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" sm="12" md="6" lg="3" xl="3">
              <validation-provider :name="$tc('message.damage', 1).toLowerCase()"
                                   :rules="{ min: 0, max: 10 }"
                                   v-slot="{ valid, errors }">
                <b-form-group :label="$tc('message.damage', 1)" label-for="input-damage">
                  <b-input-group>
                    <b-form-input id="input-damage"
                                  v-model="input.damage"
                                  autocomplete="off" type="number" min="0"
                                  :placeholder="`${$tc('message.example', 1)}: 150000`"
                                  :state="itemChanged && !valid ? false : null">
                    </b-form-input>
                    <div class="input-group-append">
                      <div class="input-group-text">€</div>
                    </div>
                  </b-input-group>

                  <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" sm="12" md="6" lg="3" xl="3">
              <b-form-group>
                <label slot="label" class="m-0 p-0">
                  {{$tc('message.compensation', 1)}}
                </label>

                <b-input-group>
                  <b-form-input id="input-compensation"
                                v-model="input.compensation"
                                autocomplete="off"
                                :placeholder="`${$tc('message.example', 1)}: 150000`"
                                type="number"
                                max="2500000"
                                min="0"
                                :state="!input.compensation ? null : input.compensation.length && input.compensation.length <= 7">
                  </b-form-input>
                  <div class="input-group-append">
                    <div class="input-group-text">€</div>
                  </div>
                </b-input-group>

                <div v-if="input.compensation ? input.compensation.length > 7 : false" class="my-invalid-feedback">
                  <h6>{{$tc('message.maxLength', 1)}} 7</h6>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row class="m-1 mb-4">
            <b-col class="border-bottom border-c-green">
              <label class="tiny-label c-green">{{$tc('message.counterpart')}}</label>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" sm="12" md="6" lg="3" xl="3">
              <validation-provider :name="$tc('message.name', 1).toLowerCase()"
                                   :rules="{ min: 0, max: 65, required: true }"
                                   v-slot="{ valid, errors }">
                <b-form-group :label="`${$tc('message.name', 1)} (*)`" label-for="input-counterparty-name">
                  <b-input-group>
                    <b-form-input id="input-counterparty-name"
                                  v-model="input.counterparty.name"
                                  autocomplete="off"
                                  :placeholder="`${$tc('message.example', 1)}: Rossi luigi`"
                                  :state="itemChanged ? valid : null">
                    </b-form-input>
                  </b-input-group>

                  <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" sm="12" md="6" lg="3" xl="3">
              <validation-provider :name="$tc('message.numberPlate', 1).toLowerCase()"
                                   :rules="{ min: 0, max: 8, required: true }"
                                   v-slot="{ valid, errors }">
                <b-form-group :label="`${$tc('message.numberPlate', 1)} (*)`" label-for="input-counterparty-name">
                  <b-input-group>
                    <b-form-input id="input-counterparty-plateNumber"
                                  v-model="input.counterparty.plateNumber"
                                  autocomplete="off"
                                  :placeholder="`${$tc('message.example', 1)}: EK629XP`"
                                  :state="itemChanged ? valid : null">
                    </b-form-input>
                  </b-input-group>

                  <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" sm="12" md="6" lg="3" xl="3">
              <validation-provider :name="$tc('message.model', 1).toLowerCase()"
                                   :rules="{ min: 0, max: 25 }"
                                   v-slot="{ valid, errors }">
                <b-form-group :label="`${$tc('message.model', 1)}`" label-for="input-counterparty-vehicleModel">
                  <b-input-group>
                    <b-form-input id="input-counterparty-vehicleModel"
                                  v-model="input.counterparty.vehicleModel"
                                  autocomplete="off"
                                  :placeholder="`${$tc('message.example', 1)}: Fiat Punto`"
                                  :state="itemChanged && !valid ? false : null">
                    </b-form-input>
                  </b-input-group>

                  <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" sm="12" md="6" lg="3" xl="3">
              <b-form-group :label="`${$tc('message.insuranceCompany', 1)}`"
                            label-for="insurance-company">
                <model-list-select id="insurance-company"
                                   option-text="text"
                                   option-value="value"
                                   :placeholder="insuranceCompanyOptions && insuranceCompanyOptions.length
                                                 !== 0 ? $t('message.selectOption') : $t('message.noOption')"
                                   v-model="input.counterparty.insuranceCompany"
                                   :list="insuranceCompanyOptions">
                </model-list-select>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <validation-provider :name="$tc('message.note', 2).toLowerCase()"
                                   :rules="{ min: 0, max: 2500 }"
                                   v-slot="{ valid, errors }">
                <b-form-group :label="`${$tc('message.note', 2)}`"
                              label-for="service-note">
                  <b-form-textarea id="service-note"
                                   v-model="input.note"
                                   rows="3" type="text"
                                   :placeholder="`${$tc('message.example', 1)}: ABCD - 1234 - ABCD - 1234 - ABCD - 1234 - ABCD - 1234`"
                                   :state="itemChanged && !valid ? false : null">
                  </b-form-textarea>

                  <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row class="mt-0">
            <b-col class="mt-3 justify-content-end d-flex">
              <!-- config[crud], create - read - update - delete -->
              <b-button class="b-c-green mr-2"
                        v-on:click="saveAccident(false)" id="btn-save"
                        v-if="accidentId ? config.u : config.c"
                        :disabled="checkSaveButton(true) || invalid">{{$t('message.save')}}
              </b-button>

              <!-- config[crud], create - read - update - delete -->
              <b-button class="b-c-green mr-2"
                        v-on:click="saveAccident(true)"
                        v-if="accidentId ? config.u : config.c"
                        id="btn-save-and-close"
                        :disabled="checkSaveButton(true) || invalid">{{$t('message.saveClose')}}
              </b-button>

              <b-button class="b-c-green" v-on:click="pushBack()" id="btn-close">
                {{$t('message.close')}}
              </b-button>
            </b-col>
          </b-row>
        </b-container>
      </validation-observer>
    </b-card>
  </b-container>
</template>

<script>
import Vue from 'vue'
import { find, first } from 'lodash'
import { DateTime } from 'luxon'
import { Italian } from 'flatpickr/dist/l10n/it.js'

export default {
  name: 'EditAccident',
  created () {
    this.fetchData()
  },
  updated () {
    this.$root.$on('change-lang', () => {
      if (this.$el.isConnected) {
        this.getOptions()
        this.updateLinks()
      }
    })
  },
  watch: {
    'input.vehicleId' (newVal) {
      this.dateOption.minDate = null
      if (newVal) {
        const foundedVehicle = find(this.vehicleOptions, obj => obj.value === newVal)
        if (foundedVehicle && foundedVehicle.registrationDate) {
          this.dateOption.minDate = foundedVehicle.registrationDate
        }
      }
    }
  },
  data () {
    const accidentId = this.$route.params.id !== 'new' ? this.$route.params.id : null
    const config = this.$route.meta.config

    return {
      config,
      first,
      accidentId,
      DateTime,
      dateOption: {
        dateFormat: 'Y-m-d',
        mode: 'single',
        altFormat: 'D j M Y',
        altInput: true,
        locale: Italian,
        minDate: '',
        maxDate: DateTime.local().toSQLDate(),
        disable: []
      },
      input: {
        vehicleId: '',
        counterparty: {}
      },
      vehicleOptions: [],
      driverOptions: [],
      statusOptions: [],
      insuranceCompanyOptions: [],
      links: [],
      watcher: null,
      itemChanged: false
    }
  },
  methods: {
    clickedLink (link) {
      const vehicleId = this.input.vehicleId
      if (link.href === '/organization/registry_vehicles' && vehicleId) {
        this.pushBack({ name: 'EditVehicle', params: { id: vehicleId } })
      } else if (link.href && link.href !== '/organization/registry_vehicles') {
        this.pushBack(link.href)
      }
    },
    checkWatcher () {
      this.$root.$emit('activeLoader', true)
      if (this.watcher) this.watcher()
      this.itemChanged = false
    },
    addWatcher () {
      this.$root.$emit('activeLoader', false)
      this.watcher = this.$watch('input', () => {
        this.itemChanged = true
      }, { deep: true })
    },
    fetchData () {
      this.checkWatcher()
      this.getOptions().then(() => {
        this.checkWatcher()
        this.getAccident()
      })
    },
    getOptions () {
      const axiosArray = []
      axiosArray.push(this.getVehicles())
      axiosArray.push(this.getDrivers())
      axiosArray.push(this.getEnum('insurance_company', 'insuranceCompanyOptions', true))
      axiosArray.push(this.getEnum('accident_state', 'statusOptions', true))
      return Vue.myAll(axiosArray).then(r => r).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        return false
      })
    },
    getVehicleOption (e) {
      return `${e.text} ${e.enabled ? '' : this.$tc('message.disabled', 1)}`
    },
    getEnum (path, obj, selectionMessage) {
      const options = []
      return Vue.myGet(this, `/enums/${path}`, 'data').then((items) => {
        if (selectionMessage) {
          options.push({ value: '', text: items.length !== 0 ? this.$t('message.selectOption') : this.$t('message.noOption') })
        }
        items.forEach((item) => {
          options.push({ value: item.id, text: item.descr })
        })
        if (obj) {
          this[obj] = options
        }
        return options
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        return false
      })
    },
    getVehicles () {
      const editedVehicleOptions = []
      return Vue.myGet(this, '/vehicles?all=true', 'vehicles').then((vehicles) => {
        vehicles.forEach((vehicle) => {
          const brand = vehicle.type && vehicle.type.brand ? vehicle.type.brand : null
          const model = vehicle.type && vehicle.type.model ? vehicle.type.model : null
          const name = `${vehicle.plateNumber} ${brand ? '- ' + brand : ''} ${model ? '- ' + model : ''}`
          const editedVehicleOption = {
            value: vehicle.id,
            text: name,
            registrationDate: vehicle.registrationDate,
            enabled: !vehicle.disablingDate
          }
          if ((!this.accidentId && editedVehicleOption.enabled) || !this.accidentId) {
            editedVehicleOptions.push(editedVehicleOption)
          }
        })
        this.vehicleOptions = editedVehicleOptions
        return editedVehicleOptions
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        return false
      })
    },
    getDrivers () {
      const editedDriverOptions = []
      return Vue.myGet(this, '/drivers', 'drivers').then((drivers) => {
        editedDriverOptions.push({ value: '', text: drivers.length !== 0 ? this.$t('message.selectOption') : this.$t('message.noOption') })
        drivers.forEach((driver) => {
          const name = `${driver.surname} - ${driver.name}`
          const editedDriverOption = {
            value: driver.id,
            text: name
          }
          editedDriverOptions.push(editedDriverOption)
        })
        this.driverOptions = editedDriverOptions
        return editedDriverOptions
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        return false
      })
    },
    getAccident () {
      if (this.accidentId) {
        Vue.myGet(this, `/accidents/${this.accidentId}`, 'accidents').then((accidents) => {
          const editedItem = first(accidents)
          editedItem.ts = editedItem.ts ? DateTime.fromMillis(editedItem.ts).toSQLDate() : null
          editedItem.counterparty = editedItem.counterparty || {}
          editedItem.damage = editedItem.damage ? parseInt(editedItem.damage) : null
          editedItem.compensation = editedItem.compensation ? parseInt(editedItem.compensation) : null
          this.input = editedItem
          this.updateLinks()
          this.addWatcher()
        }).catch(error => { Vue.manageErrors(this, error, Vue.getDimetiorHttpCode()) })
      } else {
        this.input = {
          vehicleId: '',
          counterparty: {}
        }
        this.updateLinks()
        this.addWatcher()
      }
    },
    checkSaveButton (checkItemChanged) {
      const changed = this.itemChanged
      const hasVehicleId = this.input.vehicleId
      const counterParty = this.input.counterparty
      const counterPartyName = counterParty && counterParty.name ? counterParty.name : null
      const counterPartyPlateNumber = counterParty && counterParty.plateNumber ? counterParty.plateNumber.length > 8 : null
      const placeErrors = this.input.place ? this.input.place.length > 65 : false
      const damageErrors = this.input.damage ? this.input.damage.length > 7 : false
      const compensationErrors = this.input.compensation ? this.input.compensation.length > 7 : false
      const modelErrors = counterParty.vehicleModel ? counterParty.vehicleModel.length > 65 : false
      const noteErrors = this.input.note ? this.input.note.length > 2500 : false

      if (checkItemChanged && !changed) return true
      return !hasVehicleId || !counterPartyName || counterPartyPlateNumber ||
        placeErrors || damageErrors || compensationErrors || modelErrors || noteErrors
    },
    saveAndClose () {
      this.saveAccident(true)
    },
    saveAccident (pushBack, abortRefresh, pushTo) {
      const newAccident = this.assembleAccident()
      return Vue.putOrPost(this, !!newAccident.id, `/accidents${newAccident.id ? '/' + newAccident.id : ''}`, newAccident).then((r) => {
        if (pushBack) {
          this.itemChanged = false
          this.pushBack()
          Vue.success(this, `${this.$tc('message.accident', 1)} ${this.$t(newAccident.id ? 'success.putSuccess' : 'success.postSuccess')}`)
        } else if (!abortRefresh) {
          this.$router.push(pushTo || { name: 'RefreshPage', params: { id: newAccident.id || r.data.id, name: 'EditAccident' } })
          Vue.success(this, `${this.$tc('message.accident', 1)} ${this.$t(newAccident.id ? 'success.putSuccess' : 'success.postSuccess')}`)
        }
        return true
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        return false
      })
    },
    assembleAccident () {
      const accident = {
        vehicleId: this.input.vehicleId,
        driverId: this.input.driverId || null,
        state: this.input.state || null,
        ts: this.input.ts ? DateTime.fromSQL(this.input.ts).toMillis() : null,
        place: this.input.place || null,
        damage: this.input.damage ? parseInt(this.input.damage) : null,
        compensation: this.input.compensation ? parseInt(this.input.compensation) : null,
        counterparty: {
          name: this.input.counterparty.name || null,
          plateNumber: this.input.counterparty.plateNumber || null,
          vehicleModel: this.input.counterparty.vehicleModel || null,
          insuranceCompany: this.input.counterparty.insuranceCompany || null
        },
        note: this.input.note || null
      }
      if (this.accidentId) accident.id = this.accidentId
      return accident
    },
    pushBack (obj) {
      const backObj = obj || { name: 'Accident' }
      if (this.itemChanged) this.$root.$emit('go-back-modal', backObj)
      else this.$router.push(backObj)
    },
    updateLinks () {
      const links = []
      this.links.forEach(link => {
        links.push({ href: link })
      })
      this.$root.$emit('change-links', links)
    },
    changeFlatClass (ref, myClass) {
      if (this.$refs[ref] && this.$refs[ref].$el && this.$refs[ref].$el.parentElement &&
            this.$refs[ref].$el.parentElement.children && this.$refs[ref].$el.parentElement.children[1]) {
        this.$refs[ref].$el.parentElement.children[1].className = myClass
      }
      return myClass
    },
    checkData (first) {
      const changed = this.itemChanged
      if (!changed) return { class: 'form-control', state: null }
      if (first) {
        return { class: 'form-control is-valid', state: true }
      } else if (!first && changed) {
        return { class: 'form-control is-invalid', state: false }
      }
      return { class: 'form-control', state: null }
    }
  }
}
</script>

<style scoped>
  .form-control.is-valid, .was-validated .form-control:valid {
    border-color: #28a745 !important;
    padding-right: 2.25rem !important;
  }

  .form-control.is-invalid, .was-validated .form-control:invalid {
    border-color: #dc3545 !important;
    padding-right: 2.25rem !important;
  }
</style>
